import {useState} from 'react';
import {useForm} from 'react-hook-form';
import {ProductsServiceClientImpl} from '../../../grpc_client/grips/protos/products/v1/products_service';
import useProductReviewsTracking from '../../../tracking/hooks/useProductReviewsTracking';
import {
  useGripsClientContext,
  useGrpcClientChannel,
} from '../../../ui/sharedContexts/GrpcClientContext';
import {useUserSessionContext} from '../../../ui/sharedContexts/UserSessionContext';
import {useDevice} from '../../../utils/hooks/useDevice';
import {ProductReviewFormData} from '../types';
import {useIovationFingerprint} from './useIovationFingerprint';
import useProductReviews from './useProductReviews';

const GRPS_ALREADY_VOTED = 6;

const useSendProductReview = (
  productId: number,
  defaultValues: Partial<ProductReviewFormData>,
  callback: (status: 'success' | 'error' | 'alreadyVoted') => void,
) => {
  const {handleSubmit, register, getValues, errors} =
    useForm<ProductReviewFormData>({
      mode: 'onSubmit',
      defaultValues,
    });
  const channel = useGrpcClientChannel();
  const {childShopId, shopId} = useGripsClientContext();
  const {apiToken} = useUserSessionContext();
  const {device} = useDevice();
  const {handleProductRated, productReview} = useProductReviews(productId);
  const [loadingState, setLoadingState] = useState(false);
  const trackSubmit = useProductReviewsTracking('submit', productId);
  const fingerprint = useIovationFingerprint();

  const client = new ProductsServiceClientImpl(channel);

  const onSubmit = (data: ProductReviewFormData) => {
    if (!productReview) {
      return;
    }

    setLoadingState(true);

    client
      .submitReview({
        config: {
          childShopId,
          shopId,
          session: {
            $case: 'customerSession',
            customerSession: {
              authToken: apiToken,
            },
          },
          device,
        },
        productId,
        text: data.reviewText,
        authorName: data.displayName,
        overallRatingStars: parseFloat(data.rating),
        qualityRatingStars: parseFloat(data.quality),
        fitRating: parseFloat(data.size),
        title: data.reviewTitle,
        // @ts-ignore
        verifiedPurchaseToken: productReview.verifiedPurchaseToken.split(','),
        deviceFingerprint: fingerprint || '',
      })
      .then(() => {
        handleProductRated();
        callback('success');
        trackSubmit();
      })
      .catch(error => {
        if (error.code === GRPS_ALREADY_VOTED) {
          callback('alreadyVoted');
        } else {
          callback('error');
        }
      })
      .finally(() => {
        setLoadingState(false);
      });
  };

  return {
    submitHandler: handleSubmit(onSubmit),
    register,
    errors,
    loadingState,
    getValues,
  };
};

export default useSendProductReview;
